import { AccountDataMigrationStage, IAccountDataMigrationProgress } from './models';

export const STAGE_LABELS: Record<AccountDataMigrationStage, string> = {
	Starting: 'Starting',
	ListingGrowers: 'Listing growers',
	CopyingGrowers: 'Copying growers',
	CopyingSellerAnalysis: 'Copying seller AI analysis',
	Completing: 'Marking users as migrated',
	Finished: 'Finished',
};

export interface IGrowerProgress
{
	processed: number;
	total: number;
}

/**
 * Growers processed so far. `CurrentProgress` ("12/56" = 12 of 56 growers already processed) is the source
 * of truth; the numeric counts are the fallback when it is missing or malformed.
 */
export function growerProgress(p: IAccountDataMigrationProgress | null | undefined): IGrowerProgress | null
{
	if (!p)
	{
		return null;
	}
	const match = /^\s*(\d+)\s*\/\s*(\d+)\s*$/.exec(p.CurrentProgress ?? '');
	if (match)
	{
		return { processed: Number(match[1]), total: Number(match[2]) };
	}
	return { processed: p.CompletedGrowers ?? 0, total: p.TotalGrowers ?? 0 };
}

/** Bar position (0-100) for a running migration. */
export function progressPercent(p: IAccountDataMigrationProgress | null | undefined): number
{
	const g = growerProgress(p);
	if (!g || g.total <= 0)
	{
		return p?.Stage === 'Finished' ? 100 : 0;
	}
	const growers = Math.min(100, Math.round((100 * g.processed) / g.total));
	// Keep a little headroom while the post-copy stages run, so the bar does not sit at 100% for a while.
	switch (p.Stage)
	{
		case 'CopyingSellerAnalysis':
			return Math.max(growers, 96);
		case 'Completing':
			return 98;
		case 'Finished':
			return 100;
		default:
			return growers;
	}
}

/** One-line description under the bar, e.g. "12/56 growers processed · 2 failed · Copying growers". */
export function progressText(p: IAccountDataMigrationProgress | null | undefined): string
{
	const g = growerProgress(p);
	if (!g)
	{
		return 'Running';
	}
	const failed = p.FailedCopies > 0 ? ` · ${p.FailedCopies} failed` : '';
	return `${g.processed}/${g.total} growers processed${failed} · ${STAGE_LABELS[p.Stage] ?? p.Stage}`;
}
